/**
 * Recon API Abstraction Layer
 *
 * Provides recon-specific data from the tools dataset.
 * Reads delegate to toolsApi. Writes call the update_tool Azure Function directly.
 */

import { getTools, getToolById, transformTool } from '../../asset-registry/api/toolsApi';

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
/**
 * Get tools for recon (filters for tools needing reconciliation).
 */
export async function getReconTools(options = {}, callerEmail = null) {
  const allTools = await getTools({}, callerEmail);
  let filtered = allTools.tools;

  if (options.listNumber) {
    filtered = filtered.filter(t => t.listNumber === options.listNumber);
  }
  if (options.reconStatus) {
    filtered = filtered.filter(t => t.reconStatus === options.reconStatus);
  }
  if (options.oem) {
    filtered = filtered.filter(t => t.oem === options.oem);
  }
  if (options.toolType) {
    filtered = filtered.filter(t => t.toolType === options.toolType);
  }
  if (options.searchText) {
    const search = options.searchText.toLowerCase();
    filtered = filtered.filter(t =>
      t.toolId.toLowerCase().includes(search) ||
      (t.listNumber && t.listNumber.toLowerCase().includes(search)) ||
      (t.reconStatus && t.reconStatus.toLowerCase().includes(search)) ||
      (t.oem && t.oem.toLowerCase().includes(search)) ||
      (t.supplierName && t.supplierName.toLowerCase().includes(search))
    );
  }

  const reconTools = filtered.map(tool => transformToolForRecon(tool));
  const stats = calculateReconStats(reconTools);

  return { tools: reconTools, stats };
}

/**
 * Transform tool to recon format (adds recon-specific display fields).
 * Preserves `id` for writes.
 */
function transformToolForRecon(tool) {
  return {
    ...tool,
    id: tool.id,                                        // preserve internal Cosmos ID for writes
    listNumber: tool.listNumber || null,
    reconStatus: (tool.reconStatus || 'OPEN').trim(),
    platform: getPlatform(tool.toolId),
    invPnr1: tool.inventoryPartNumbers?.[0] || null,
    invPnr2: tool.inventoryPartNumbers?.[1] || null,
    fgPn1: getFgPn(tool, 0),
    fgPn2: getFgPn(tool, 1),
    fgPn3: getFgPn(tool, 2),
    fgPn4: getFgPn(tool, 3),
    fgPn5: getFgPn(tool, 4),
    fgPn6: getFgPn(tool, 5),
    fgPn7: getFgPn(tool, 6),
    fgPn8: getFgPn(tool, 7),
    thumbnails: getThumbnails(tool),
    tierLevel: '',
    tier1Pnr1: '',
    tier1Pnr2: '',
    shipToLocation: '',
    lastShipDate: '',
    componentPnr1: '',
    componentPnr2: '',
    capacity: '',
    npn: '',
    sectionDetail: tool.sectionDetail || tool.detailLocation || '',
    supplierStatus: tool.supplierStatus || '',
    supplierComments: tool.supplierComments || '',
    reconComments: tool.supplierComments || tool.reconComments || '',
  };
}

/**
 * Update tool during reconciliation.
 * Calls the real Azure Function using the internal Cosmos `id`.
 * Transforms the response back to camelCase so the UI updates correctly.
 */
export async function updateToolRecon(toolId, updates, callerEmail = null) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/update_tool?${params}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update tool');
  }

  // Function returns raw Cosmos document (snake_case).
  // Transform it back to camelCase so the modal displays updated values.
  const rawDoc = await response.json();
  return transformToolForRecon(transformTool(rawDoc));
}

/**
 * Get single tool for reconciliation.
 */
export async function getToolForRecon(toolId, callerEmail = null) {
  const tool = await getToolById(toolId, callerEmail);
  return transformToolForRecon(tool);
}

/**
 * Get filter options for recon.
 */
export async function getReconFilterOptions(callerEmail = null) {
  const allTools = await getTools({}, callerEmail);
  const reconTools = allTools.tools.map(tool => transformToolForRecon(tool));

  return {
    listNumbers: [...new Set(reconTools.map(t => t.listNumber))].filter(Boolean).sort(),
    reconStatuses: [...new Set(reconTools.map(t => t.reconStatus))].filter(Boolean).sort(),
    oems: [...new Set(reconTools.map(t => t.oem))].filter(Boolean).sort(),
    toolTypes: [...new Set(reconTools.map(t => t.toolType))].filter(Boolean).sort()
  };
}

// ─── Asset Tags ─────────────────────────────────────────────────────────────

export async function getAssetTags(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?${params}`);
  if (!res.ok) throw new Error('Failed to fetch asset tags');
  return res.json();
}

export async function addAssetTag({ toolId, siteId, tagValue, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tool_id: toolId, site_id: siteId, tag_value: tagValue, caller_email: callerEmail })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to add asset tag');
  }
  return res.json();
}

export async function patchAssetTag({ tagId, tagValue, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag_id: tagId, tag_value: tagValue, caller_email: callerEmail })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update asset tag');
  }
  return res.json();
}

export async function deleteAssetTag({ tagId, toolId, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag_id: tagId, tool_id: toolId, caller_email: callerEmail })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to delete asset tag');
  }
  return res.json();
}

// ─── Part Numbers ───────────────────────────────────────────────────────────

export async function getPartNumbers(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?${params}`);
  if (!res.ok) throw new Error('Failed to fetch part numbers');
  return res.json();
}

export async function addPartNumber({ toolId, siteId, partNumber, partNumberType, partDescription, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tool_id: toolId, site_id: siteId, part_number: partNumber,
      part_number_type: partNumberType || '', part_description: partDescription || '',
      caller_email: callerEmail
    })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to add part number');
  }
  return res.json();
}

export async function patchPartNumber({ partNumberId, partNumber, partNumberType, partDescription, callerEmail }) {
  const body = { part_number_id: partNumberId, caller_email: callerEmail };
  if (partNumber !== undefined) body.part_number = partNumber;
  if (partNumberType !== undefined) body.part_number_type = partNumberType;
  if (partDescription !== undefined) body.part_description = partDescription;
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update part number');
  }
  return res.json();
}

export async function deletePartNumber({ partNumberId, toolId, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ part_number_id: partNumberId, tool_id: toolId, caller_email: callerEmail })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to delete part number');
  }
  return res.json();
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getPlatform(toolId) {
  const platforms = ['150L', '715L', '591L', '397L', ''];
  const hash = toolId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return platforms[hash % platforms.length];
}

function getFgPn(tool, index) {
  if (tool.inventoryPartNumbers && tool.inventoryPartNumbers[index]) {
    return tool.inventoryPartNumbers[index];
  }
  return null;
}

function getThumbnails(tool) {
  if (!tool.photos || tool.photos.length === 0) return [];
  return tool.photos.map(photo => `${photo.url}?width=32&height=32`).slice(0, 3);
}

function calculateReconStats(tools) {
  const total = tools.length;
  const closedTools = tools.filter(t => t.reconStatus === 'CLOSED');
  const openTools = tools.filter(t => t.reconStatus === 'OPEN');

  return {
    assetsUnderReview: total,
    reconAssetsClosed: closedTools.length,
    reconAssetsOpen: openTools.length,
    reconPercentComplete: total > 0 ? Math.round((closedTools.length / total) * 100) : 0
  };
}
